import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Card, CardContent, Grid,
  LinearProgress, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, MenuItem, TextField, Chip,
} from '@mui/material';
import { AccountBalance } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';
function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const fmtHKD = (n: any) => n != null ? `HK$${Number(n).toLocaleString('en-HK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEAR = new Date().getFullYear();

export default function LabourCost() {
  const [year, setYear] = useState(YEAR);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    apiFetch(`/api/labour-cost/summary?year=${year}`)
      .then(d => { setData(d); setLoading(false); })
      .catch((e: any) => { setError(e.message); setLoading(false); });
  }, [year]);

  const maxDeptCost = data ? Math.max(...(data.byDept ?? []).map((d: any) => Number(d.totalCost ?? 0)), 1) : 1;
  const maxMonth = data ? Math.max(...(data.monthly ?? []).map((m: any) => Number(m.gross ?? 0)), 1) : 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance sx={{ fontSize: 32, color: '#1a237e' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Labour Cost Report</Typography>
            <Typography variant="body2" color="text.secondary">Payroll spend by department, role, and month</Typography>
          </Box>
        </Box>
        <TextField label="Year" select size="small" value={year} onChange={e => setYear(Number(e.target.value))} sx={{ width: 100 }}>
          {[YEAR - 1, YEAR].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {data && (
        <>
          {/* YTD KPIs */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'YTD Gross Pay',    value: fmtHKD(data.ytd.ytdGross),         color: '#1a237e' },
              { label: 'YTD Net Pay',      value: fmtHKD(data.ytd.ytdNet),           color: '#2e7d32' },
              { label: 'Payroll Runs',     value: data.ytd.runCount ?? 0,             color: '#e65100' },
              { label: 'Avg Headcount',    value: data.ytd.avgHeadcount ?? 0,         color: '#37474f' },
            ].map(s => (
              <Grid key={s.label} size={{ xs: 6, sm: 3 }}>
                <Card sx={{ textAlign: 'center', p: 1 }}>
                  <CardContent sx={{ pb: '8px !important' }}>
                    <Typography variant="h5" fontWeight={700} color={s.color}>{s.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            {/* Monthly cost bars */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper sx={{ p: 2 }}>
                <Typography fontWeight={700} sx={{ mb: 2 }}>Monthly Gross Pay — {year}</Typography>
                {data.monthly.length === 0 && <Typography color="text.secondary" variant="body2">No payroll data for {year}.</Typography>}
                {data.monthly.map((m: any) => (
                  <Box key={m.month} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" sx={{ width: 28, flexShrink: 0 }}>{MONTHS[m.month - 1]}</Typography>
                    <Box sx={{ flex: 1, bgcolor: '#f5f5f5', borderRadius: 1, height: 20, overflow: 'hidden' }}>
                      <Box sx={{ width: `${Math.round((Number(m.gross) / maxMonth) * 100)}%`, bgcolor: '#1a237e', height: '100%', borderRadius: 1, minWidth: 4 }} />
                    </Box>
                    <Typography variant="caption" sx={{ width: 90, textAlign: 'right', flexShrink: 0 }}>{fmtHKD(m.gross)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 30, flexShrink: 0 }}>{m.headcount}p</Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>

            {/* Headcount by employment type */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Typography fontWeight={700} sx={{ mb: 2 }}>Active Headcount by Type</Typography>
                {(data.byType ?? []).map((t: any) => (
                  <Box key={t.employmentType} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Chip label={t.employmentType ?? 'Unknown'} size="small" variant="outlined" />
                    <Typography fontWeight={700}>{t.count}</Typography>
                  </Box>
                ))}
                {data.byType?.length === 0 && <Typography color="text.secondary" variant="body2">No active employees.</Typography>}
              </Paper>
            </Grid>

            {/* By Department */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography fontWeight={700} sx={{ mb: 2 }}>Cost by Department — {year}</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><b>Department</b></TableCell>
                        <TableCell align="right"><b>Headcount</b></TableCell>
                        <TableCell align="right"><b>Total Cost</b></TableCell>
                        <TableCell align="right"><b>Avg Salary</b></TableCell>
                        <TableCell sx={{ width: 80 }}><b>Share</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.byDept ?? []).map((d: any) => {
                        const pct = Math.round((Number(d.totalCost ?? 0) / maxDeptCost) * 100);
                        return (
                          <TableRow key={d.department} hover>
                            <TableCell>{d.department ?? 'Unknown'}</TableCell>
                            <TableCell align="right">{d.headcount}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtHKD(d.totalCost)}</TableCell>
                            <TableCell align="right">{fmtHKD(d.avgSalary)}</TableCell>
                            <TableCell>
                              <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, height: 8, overflow: 'hidden' }}>
                                <Box sx={{ width: `${pct}%`, bgcolor: '#1a237e', height: '100%', minWidth: 2 }} />
                              </Box>
                              <Typography variant="caption" color="text.secondary">{pct}%</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {data.byDept?.length === 0 && (
                        <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No data for {year}.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>

            {/* By Role */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography fontWeight={700} sx={{ mb: 2 }}>Top Roles by Cost — {year}</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><b>Job Title</b></TableCell>
                        <TableCell align="right"><b>Count</b></TableCell>
                        <TableCell align="right"><b>Total Cost</b></TableCell>
                        <TableCell align="right"><b>Avg Salary</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.byRole ?? []).map((r: any) => (
                        <TableRow key={r.jobTitle} hover>
                          <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{r.jobTitle ?? 'Unknown'}</Typography></TableCell>
                          <TableCell align="right">{r.headcount}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtHKD(r.totalCost)}</TableCell>
                          <TableCell align="right">{fmtHKD(r.avgSalary)}</TableCell>
                        </TableRow>
                      ))}
                      {data.byRole?.length === 0 && (
                        <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>No data.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
